<template>
  <div
    class="login"
    :class="[isWide && 'login--wide', isLight ? 'login--light' : 'login--dark', isEverydayHeroes && 'login--everyday-heroes']"
  >
    <video
      v-if="!isLight && !isEverydayHeroes"
      class="login__bg-video"
      muted
      autoplay
      loop
      playsinline
      src="https://cloud-1de12d.becdn.net/customfile/18528de44979c54adf15e871f462cfff9755bb764cb3e4d0258a5f2dd9c7b79f/Home-Page-Video-BG.mp4"
    />
    <div
      class="login__logo"
    >
      <a
        v-if="isEverydayHeroes"
        class="login__campaign"
        :href="logoUrl"
        target="_blank"
        rel="noopener"
        aria-label="Know more about NATA's Everyday Heroes (opens in a new tab)"
      >
        <img
          src="@/assets/everyday-heroes-bg.jpg"
          alt="NATA Accreditation Matters: you are our everyday heroes."
          width="1232"
          height="1080"
        >
      </a>
      <v-logo
        v-else
        :is-light="logoIsLight"
        :logo-name="logoName"
        :logo-url="logoUrl"
      />
    </div>
    <div class="login__wrap">
      <div class="login__form">
        <v-headline
          v-if="title"
          :level="1"
          is-centered
        >
          {{ title }}
        </v-headline>
        <div class="login__inner">
          <v-alert
            v-if="errors"
            color="error"
            icon="notification"
          >
            {{ errors }}
          </v-alert>
          <slot />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import Headline from "@/atoms/Headline";
  import Logo from "@/atoms/Logo";
  import Alert from "@/atoms/Alert";

  import {mapState} from "vuex";

  export default {
    components: {
      "v-headline": Headline,
      "v-logo": Logo,
      'v-alert': Alert,
    },
    props: {
      title: {
        type: String,
        default: null
      },
      isWide: {
        type: Boolean,
        default: false
      },
      isLight: {
        type: Boolean,
        default: true
      },
      isEverydayHeroes: {
        type: Boolean,
        default: false
      },
      logoName: {
        type: String,
        default: null
      },
      logoUrl: {
        type: String,
        default: null
      }
    },
    computed: {
      logoIsLight() {
        return this.isLight;
      },
      ...mapState({
        errors: state => state.auth.errors
      })
    },
  };
</script>

<style lang="scss">
  @import "../../styles/shared/shared";

  .login {
    $this: &;
    position: relative;
    display: flex;
    min-height: 100%;
    flex-direction: column;
    justify-content: center;
    flex: 1;
    overflow: hidden;

    // background layer (white or dark) moved to ::before
    &::before {
      content: "";
      position: absolute;
      top: 0;
      right: 0; // full width by default (mobile)
      bottom: 0;
      left: 0;
      background: $white;
      z-index: -1; // always behind all content
    }

    @include minpoint(tablet) {
      flex-direction: row;

      // on tablet+ the left background only covers 60%
      &::before {
        right: 40%;
      }

      &::after {
        display: block;
        content: "";
        position: absolute;
        top: 0;
        bottom: 0;
        z-index: -1; // behind content, above page background
        box-shadow: -5px 1px 10px 0 rgba(9, 33, 57, 0.32);
        background: linear-gradient(154.81deg, #0546c7 0%, #001744 100%);
        left: 60%;
        right: 0;
      }
    }
    &__logo {
      max-width: 100%;
      text-align: center;
      padding: 15px 30px;
      margin: 0 auto;
      .logo {
        svg {
          max-width: 100%;
          width: 600px;
          @include minpoint(tablet) {
            width: 600px;
          }
        }
      }
      @include minpoint(tablet) {
        flex: 1;
        display: flex;
        align-items: center;
        justify-content: center;
        &:before {
          @include pseudoBlock;
          width: 100vw;
          height: 100vw;
          position: absolute;
          z-index: 0;
          border: 100px solid var(--accent);
          --accent: $tangerine;
          transform-origin: top left;
          transform: rotate(50deg);
          top: 66%;
          left: 66%;
        }
      }
    }
    &__wrap {
      padding-left: 15px;
      padding-right: 15px;
      flex: 1;
      @include maxpoint(phone) {
        background: linear-gradient(to bottom, $white 60px, #001a4d 60px);
        padding-bottom: 100px;
        padding-top: 10px;
      }
      @include minpoint(tablet) {
        z-index: 1;
        display: flex;
        flex: 1;
        align-items: center;
        position: relative;
        transform-style: preserve-3d;
        justify-content: flex-start;
      }
    }
    &__form {
      position: relative;
      width: 100%;
      border-radius: 4px;
      background: $white;
      box-shadow: 0 0 9px 0 rgba(132, 142, 176, 0.4);
      padding: 30px 15px;
      transform-style: preserve-3d;
      form {
        flex: 1;
        display: flex;
        flex-direction: column;
        margin-bottom: 0;
      }
      @include minpoint(tablet) {
        display: flex;
        flex-direction: column;
        padding: 45px;
        margin-left: 10px;
        margin-right: 0;
        max-width: 500px;
        min-height: 48vh;
        max-height: 90vh;

        &:before {
          --accent: #fb8c33;
          border-width: 200px;
          transform-origin: bottom left;
          transform: translate(-10%, 5%) rotate(50deg) translateZ(-1em);
          bottom: 100%;
          left: 100%;
        }
      }
      &:before,
      &:after {
        width: 100vw;
        height: 100vw;
        @include pseudoBlock;
        position: absolute;
        z-index: -1;
        border: 50px solid var(--accent);
        @include minpoint(tablet) {
          width: 50vw;
          height: 50vw;
          border-width: 100px;
        }
      }
      &:after {
        transform-origin: top left;
        transform: translate(0%, -10%) rotate(50deg);
        top: 100%;
        left: 100%;
        --accent: #133373;
        @include minpoint(tablet) {
          --accent: #01386f;
        }
      }
    }
    &__inner {
      display: flex;
      flex-direction: column;
      flex: 1;
      overflow-y: auto;
      overflow-x: hidden;
    }
    &__bg-video {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;   // full width by default (mobile)
      height: 100%;
      object-fit: cover;
      z-index: -1; // above page, below content
      pointer-events: none;

      @include minpoint(tablet) {
        width: 60%; // 60% on tablet and up
      }
    }
    &--wide {
      #{$this}__logo {
        flex: 2;
      }
      #{$this}__wrap {
        flex: 5;
      }
      #{$this}__form {
        @include minpoint(tablet) {
          max-width: 600px;
          min-height: 70vh;
        }
        @include minpoint(desktop) {
          max-width: 800px;
        }
      }
    }
    &--dark {
      &::before {
        background: transparent; // let the video show through on the left
      }
      #{$this}__wrap {
        @include maxpoint(phone) {
          background: linear-gradient(to bottom, #001744 60px, #001a4d 60px);
        }
      }
    }
    &--everyday-heroes {
      z-index: 0;
      background: #00021c;

      &::before,
      #{$this}__logo::before,
      #{$this}__form::before,
      #{$this}__form::after {
        display: none;
      }

      @include minpoint(tablet) {
        &::after {
          background: #30479c url('../../assets/portal-login-bg.png') no-repeat right center / cover;
          box-shadow: none;
        }
      }

      #{$this}__logo {
        min-width: 0;
        width: 100%;
        padding: 0;
        margin: 0;

        @include minpoint(tablet) {
          flex: 0 0 60%;
        }
      }

      #{$this}__campaign {
        display: block;
        width: 100%;

        &:focus {
          outline: 3px solid $white;
          outline-offset: -6px;
        }

        img {
          display: block;
          width: 100%;
          height: auto;
        }
      }

      #{$this}__wrap {
        min-width: 0;
        padding: 40px 20px;
        background: #30479c url('../../assets/portal-login-bg.png') no-repeat right center / cover;

        @include minpoint(tablet) {
          flex: 0 0 50%;
          justify-content: flex-start;
          margin-left: -10%;
          padding: 40px 15px;
          background: none;
        }
      }

      #{$this}__form {
        max-width: 500px;
        max-height: none;
        margin: 0 auto;
        padding: 30px 20px;

        @include minpoint(tablet) {
          margin-left: 10px;
          margin-right: 0;
        }

        @include minpoint(desktop) {
          padding: 45px;
        }
      }

      #{$this}__inner {
        overflow: visible;
      }
    }
  }


</style>
